/**
 * Validation logic for Claim Editor
 * Separated from store to improve maintainability and testability
 */

export interface IClaimEditorValidationData {
    patient: any;
    guarantor?: any;
    billingProviderId: string | null;
    services: any[];
    providers: any[];
    additionalInformation?: any | null;
    guarantorPayments?: Array<{ collectionDate?: string; amount?: { amount?: string; currency?: string }; memo?: string }>;
    seller?: { externalId?: string; first?: string; last?: string; organization?: string } | null;
    facility?: any | null;
    subscribers?: any[];
}

/**
 * Validates if a facility object has all required fields
 */
function isValidFacility(facility: any): boolean {
    if (!facility) return true; // Optional, so valid if not present
    const addr = facility?.addresses?.[0];
    const line1 = Array.isArray(addr?.lines) ? addr.lines?.[0] : undefined;
    return Boolean(
        facility?.name &&
        line1 &&
        addr?.city &&
        addr?.state &&
        addr?.country &&
        addr?.postal
    );
}

/**
 * Adds facility validation blockers to the blockers array
 */
function addFacilityBlockers(facility: any, blockers: string[], prefix: string = ''): void {
    if (!facility) return;
    const facilityAddr = facility?.addresses?.[0];
    const facilityLine1 = Array.isArray(facilityAddr?.lines) ? facilityAddr.lines?.[0] : undefined;
    if (!facility?.name) blockers.push(`${prefix}facility name is required`);
    if (!facilityLine1) blockers.push(`${prefix}facility address line 1 is required`);
    if (!facilityAddr?.city) blockers.push(`${prefix}facility city is required`);
    if (!facilityAddr?.state) blockers.push(`${prefix}facility state is required`);
    if (!facilityAddr?.country) blockers.push(`${prefix}facility country is required`);
    if (!facilityAddr?.postal) blockers.push(`${prefix}facility postal code is required`);
}

/**
 * Validates if the claim can be submitted
 */
export function canSubmitClaim(data: IClaimEditorValidationData): boolean {
    const { patient, guarantor, billingProviderId, services, providers, additionalInformation, guarantorPayments, seller, facility, subscribers } = data;
    const p = patient as any;
    const g = guarantor as any;
    
    const hasPatient = Boolean(p && p.first && p.last && p.dob && p.sex);
    const hasPatientAddress = Boolean(
        p?.addresses?.[0]?.lines?.[0] && 
        p?.addresses?.[0]?.city && 
        p?.addresses?.[0]?.state && 
        p?.addresses?.[0]?.country && 
        p?.addresses?.[0]?.postal
    );
    const hasGuarantor = Boolean(g && g.first && g.last);
    const hasGuarantorAddress = Boolean(
        g?.addresses?.[0]?.lines?.[0] &&
        g?.addresses?.[0]?.city &&
        g?.addresses?.[0]?.state &&
        g?.addresses?.[0]?.country &&
        g?.addresses?.[0]?.postal
    );
    const hasAtLeastOneService = Array.isArray(services) && services.length > 0;
    const hasControlNumber = Boolean((additionalInformation as any)?.controlNumber && String((additionalInformation as any).controlNumber).trim().length > 0);
    const hasBillingProvider = Boolean(billingProviderId);
    const servicesValid = (services || []).every((s: any) => {
        const hasBasicFields = Boolean(
            s && 
            s.procedureCode && 
            s.fromDateOfService && 
            s.placeOfService && 
            (s.units || s.units === 0)
        );

        const hasDiagnosisCodes = Array.isArray(s?.diagnosisCodes) && s.diagnosisCodes.length > 0;
        
        // Validate facility if present
        const facilityValid = isValidFacility(s?.facility);

        // Validate providers if present
        const serviceProviders = Array.isArray(s?.providers) ? s.providers : [];
        const providersValid = serviceProviders.every((provider: any) =>
            Boolean(provider && provider.first && provider.last && provider.npi)
        );

        // Validate drug if drug object is present and has at least one field
        let drugValid = true;
        if (s?.drug) {
            const hasAnyDrugField = Boolean(
                s.drug.x12DrugQuantity ||
                s.drug.x12NationalDrugCode ||
                s.drug.x12DrugMeasurement
            );
            // Only validate if at least one field is present (partial drug data is invalid)
            if (hasAnyDrugField) {
                drugValid = Boolean(
                    s.drug.x12DrugQuantity &&
                    s.drug.x12NationalDrugCode &&
                    s.drug.x12DrugMeasurement
                );
            }
        }

        return hasBasicFields && hasDiagnosisCodes && facilityValid && providersValid && drugValid;
    });
    
    const providersValid = (providers || []).every((provider: any) => 
        Boolean(provider && provider.first && provider.last && provider.npi)
    );

    // Facility validation (if present, must be complete)
    const facilityValid = isValidFacility(facility);

    // Subscribers validation (if present, each must be complete)
    const subscribersValid = Array.isArray(subscribers) 
        ? subscribers.every((sub: any) => {
            const subAddr = sub?.addresses?.[0];
            const subLine1 = Array.isArray(subAddr?.lines) ? subAddr.lines?.[0] : undefined;
            return Boolean(
                sub?.first &&
                sub?.last &&
                sub?.payerId &&
                sub?.memberId &&
                subLine1 &&
                subAddr?.city &&
                subAddr?.state &&
                subAddr?.postal
            );
        })
        : true;

    // Settings validations
    let settingsValid = true;
    const payments = Array.isArray(guarantorPayments) ? guarantorPayments : [];
    for (const pay of payments) {
        const hasAny = Boolean(pay?.collectionDate || pay?.amount?.amount);
        if (hasAny) {
            const dateOk = Boolean(pay?.collectionDate);
            const amtNum = parseFloat(pay?.amount?.amount || '');
            const amountOk = !isNaN(amtNum) && amtNum > 0;
            if (!dateOk || !amountOk) { settingsValid = false; break; }
        }
    }
    if (settingsValid && seller) {
        const hasAnySeller = Boolean(seller.externalId || seller.organization || seller.first || seller.last);
        if (hasAnySeller) {
            // External System Id is required when seller section is used
            settingsValid = Boolean(seller.externalId && String(seller.externalId).trim().length > 0);
        }
    }

    // Require billing provider id, control number, and at least one valid service line
    return hasPatient && hasPatientAddress && hasGuarantor && hasGuarantorAddress && hasBillingProvider && hasControlNumber && hasAtLeastOneService && servicesValid && providersValid && facilityValid && subscribersValid && settingsValid;
}

/**
 * Gets a list of all validation blockers preventing submission
 */
export function getSubmitBlockers(data: IClaimEditorValidationData): string[] {
    const blockers: string[] = [];
    const { patient, guarantor, billingProviderId, services, providers, additionalInformation, guarantorPayments, seller, facility, subscribers } = data;
    const p = patient as any;
    const g = guarantor as any;
    
    // Patient validation
    if (!p?.first) blockers.push('Patient first name is required');
    if (!p?.last) blockers.push('Patient last name is required');
    if (!p?.dob) blockers.push('Patient date of birth is required');
    if (!p?.sex) blockers.push('Patient sex is required');
    
    // Patient address validation
    const addr = p?.addresses?.[0] || {};
    const line1 = Array.isArray(addr?.lines) ? addr.lines?.[0] : undefined;
    if (!line1) blockers.push('Patient address line 1 is required');
    if (!addr?.city) blockers.push('Patient city is required');
    if (!addr?.state) blockers.push('Patient state is required');
    if (!addr?.postal) blockers.push('Patient postal code is required');
    if (!addr?.country) blockers.push('Patient country is required');
    
    // Guarantor validation
    if (!g?.first) blockers.push('Guarantor first name is required');
    if (!g?.last) blockers.push('Guarantor last name is required');
    const gAddr = g?.addresses?.[0] || {};
    const gLine1 = Array.isArray(gAddr?.lines) ? gAddr.lines?.[0] : undefined;
    if (!gLine1) blockers.push('Guarantor address line 1 is required');
    if (!gAddr?.city) blockers.push('Guarantor city is required');
    if (!gAddr?.state) blockers.push('Guarantor state is required');
    if (!gAddr?.postal) blockers.push('Guarantor postal code is required');
    if (!gAddr?.country) blockers.push('Guarantor country is required');
    
    // Billing provider validation
    if (!billingProviderId) blockers.push('Billing provider is required');

    // Additional Information validation
    if (!((additionalInformation as any)?.controlNumber) || String((additionalInformation as any)?.controlNumber || '').trim() === '') {
        blockers.push('Control number is required');
    }

    // Services validation
    const servicesArray = Array.isArray(services) ? services : [];
    if (servicesArray.length === 0) {
        blockers.push('At least one service line is required');
    } else {
        servicesArray.forEach((s: any, i: number) => {
            const idx = i + 1;
            if (!s?.procedureCode) blockers.push(`Service line ${idx}: procedure code is required`);
            if (!s?.fromDateOfService) blockers.push(`Service line ${idx}: from date of service is required`);
            if (!s?.placeOfService) blockers.push(`Service line ${idx}: place of service is required`);
            if (!s?.units && s?.units !== 0) blockers.push(`Service line ${idx}: units are required`);
            if (!Array.isArray(s?.diagnosisCodes) || s.diagnosisCodes.length === 0) blockers.push(`Service line ${idx}: at least one diagnosis code is required`);

            // Validate Service Line Facility if present
            addFacilityBlockers(s?.facility, blockers, `Service line ${idx}: `);

            // Validate Service Line Providers if present
            const serviceProviders = Array.isArray(s?.providers) ? s.providers : [];
            serviceProviders.forEach((provider: any, pIdx: number) => {
                const providerType = provider?.type || 'Provider';
                if (!provider?.first) blockers.push(`Service line ${idx}: ${providerType} provider first name is required`);
                if (!provider?.last) blockers.push(`Service line ${idx}: ${providerType} provider last name is required`);
                if (!provider?.npi) blockers.push(`Service line ${idx}: ${providerType} provider NPI is required`);
            });

            // Validate Service Line Drug if drug object is present and has at least one field
            if (s?.drug) {
                const hasAnyDrugField = Boolean(
                    s.drug.x12DrugQuantity ||
                    s.drug.x12NationalDrugCode ||
                    s.drug.x12DrugMeasurement
                );
                // Only validate if at least one field is present (partial drug data is invalid)
                if (hasAnyDrugField) {
                    if (!s.drug.x12DrugQuantity) blockers.push(`Service line ${idx}: drug quantity is required`);
                    if (!s.drug.x12NationalDrugCode) blockers.push(`Service line ${idx}: drug NDC is required`);
                    if (!s.drug.x12DrugMeasurement) blockers.push(`Service line ${idx}: drug measurement is required`);
                }
            }
        });
    }

    // Additional providers validation
    const providersArray = Array.isArray(providers) ? providers : [];
    providersArray.forEach((provider: any, i: number) => {
        const idx = i + 1;
        const providerType = provider?.type || 'Provider';
        if (!provider?.first) blockers.push(`${providerType} ${idx}: first name is required`);
        if (!provider?.last) blockers.push(`${providerType} ${idx}: last name is required`);
        if (!provider?.npi) blockers.push(`${providerType} ${idx}: NPI is required`);
    });

    // Settings validation: guarantor pre-payments
    const payments = Array.isArray(guarantorPayments) ? guarantorPayments : [];
    payments.forEach((pay, i) => {
        const idx = i + 1;
        const hasAny = Boolean(pay?.collectionDate || pay?.amount?.amount);
        if (hasAny) {
            if (!pay?.collectionDate) blockers.push(`Guarantor pre-payment ${idx}: valid date is required`);
            const amtNum = parseFloat(pay?.amount?.amount || '');
            if (!pay?.amount?.amount || isNaN(amtNum) || amtNum <= 0) blockers.push(`Guarantor pre-payment ${idx}: amount must be greater than 0`);
        }
    });

    // Settings validation: seller
    if (seller) {
        const hasAnySeller = Boolean(seller.externalId || seller.organization || seller.first || seller.last);
        if (hasAnySeller) {
            // External System Id is required when seller section is used
            if (!seller.externalId || String(seller.externalId).trim().length === 0) {
                blockers.push('Seller: External System Id is required');
            }
        }
    }

    // Facility validation (if present, must be complete)
    addFacilityBlockers(facility, blockers);

    // Subscribers validation (if present, each must be complete)
    if (Array.isArray(subscribers) && subscribers.length > 0) {
        subscribers.forEach((sub: any, i: number) => {
            const idx = i + 1;
            const subAddr = sub?.addresses?.[0];
            const subLine1 = Array.isArray(subAddr?.lines) ? subAddr.lines?.[0] : undefined;
            if (!sub?.first) blockers.push(`Subscriber ${idx}: first name is required`);
            if (!sub?.last) blockers.push(`Subscriber ${idx}: last name is required`);
            if (!sub?.payerId) blockers.push(`Subscriber ${idx}: payer is required`);
            if (!sub?.memberId) blockers.push(`Subscriber ${idx}: member ID is required`);
            if (!subLine1) blockers.push(`Subscriber ${idx}: address line 1 is required`);
            if (!subAddr?.city) blockers.push(`Subscriber ${idx}: city is required`);
            if (!subAddr?.state) blockers.push(`Subscriber ${idx}: state is required`);
            if (!subAddr?.postal) blockers.push(`Subscriber ${idx}: postal code is required`);
        });
    }

    return blockers;
}

